/* eslint-disable max-len */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import { handleApiError } from '../../common/functions/errorHandler.function';
import apiClient from '../../common/functions/api_axios_instance.function';
import { notify } from '../../common/functions/react-toastify.function';
import {
  getDocumentBlob,
  revokeBlobUrl,
} from '../ApprovalPersonsInvoiceManagement/functions/get_document_blob.function';
import { getPOInvoiceAuditLogs } from './functions/get_po_invoice_audit_logs.function';
import { getApproverRoles } from './functions/get_approver_roles.function';
import { startInvoice, stopInvoice, rejectInvoice } from './functions/start_stop_invoice.function';
import POInvoiceHeader from './components/po_invoice_header.component';
import InvoiceDetailsCard from '../ApprovalPersonsInvoiceManagement/components/invoice_details_card.component';
import BreakdownTable from '../ApprovalPersonsInvoiceManagement/components/breakdown_table.component';
import CommentsSection from '../ApprovalPersonsInvoiceManagement/components/comments_section.component';
import DocumentsList from '../ApprovalPersonsInvoiceManagement/components/documents_list.component';
import AddMissingDocumentsAdminSection from './components/add_missing_documents_admin_section.component';
import InvoicePreview from '../ApprovalPersonsInvoiceManagement/components/invoice_preview.component';
import DocumentPreview from '../ApprovalPersonsInvoiceManagement/components/document_preview.component';
import InvoiceHistoryModal from '../ApprovalPersonsInvoiceManagement/components/invoice_history_modal.component';
import { runWithUnsavedCommentsGuard } from '../../common/functions/unsaved_comments_guard.function';
import { buildPoInvoiceSummary } from '../ApprovalPersonsInvoiceManagement/utils/po_invoice_summary.util';
import { getAllVendorInvoices } from './functions/get_all_vendor_invoices.function';
import {
  getCurrentApproverEmail,
  getPreviousApproverEmailFromAuditLogs,
} from '../../common/functions/derive_invoice_approver_emails.function';

const POInvoiceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedInvoiceData = location.state?.invoiceData; // Get invoice data from navigation state
  const poNumber = location.state?.poNumber || '';
  const invoices = location.state?.invoices || [];
  const poDetails = location.state?.poDetails || null;
  const fromTab = location.state?.fromTab || 'action-required';
  const isViewOnlyFromAllInvoices = fromTab === 'all-invoices-full';

  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('Admin Invoice Management')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [invoiceData, setInvoiceData] = useState(null);
  const [breakdownData, setBreakdownData] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [approverRoles, setApproverRoles] = useState([]);
  const [filteredApproverRoles, setFilteredApproverRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [assigningRole, setAssigningRole] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null); // 'start', 'stop', 'reject', or null
  const commentsSectionRef = useRef(null);
  // Start as null so we don't show a partial count (e.g. pending-only) and then flicker.
  const [poInvoicesForSummary, setPoInvoicesForSummary] = useState(null);
  const [poSummaryLoading, setPoSummaryLoading] = useState(true);
  const [invoiceSource, setInvoiceSource] = useState(passedInvoiceData);

  // Reject dialog state (pending_rejection only)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Get current invoice number
  const currentInvoiceNumber =
    passedInvoiceData?.invoice_number || passedInvoiceData?.invoiceNumber || '';

  useEffect(() => {
    setInvoiceSource(passedInvoiceData || null);
  }, [passedInvoiceData]);

  const invoicePayload = invoiceSource || passedInvoiceData;

  // Prefer id from current payload (unique even when invoice_number is duplicated)
  const currentInvoiceId = invoicePayload?.id ?? invoicePayload?._originalData?.id ?? null;

  // For PO Summary counts/amounts: always use ALL invoices for this PO (not just the pending list
  // passed from the previous screen).
  useEffect(() => {
    const poNum = String(
      poNumber || invoicePayload?.po_number || invoicePayload?.poNumber || ''
    ).trim();
    if (!poNum) {
      setPoInvoicesForSummary(null);
      setPoSummaryLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadAllForPo = async () => {
      setPoSummaryLoading(true);
      try {
        const list = await getAllVendorInvoices();
        const forPo = (Array.isArray(list) ? list : []).filter(
          (inv) => String(inv.po_number || inv.poNumber || '').trim() === poNum
        );
        if (!cancelled) {
          setPoInvoicesForSummary(forPo);
          setPoSummaryLoading(false);
        }
      } catch (e) {
        // Fallback: keep whatever we got from navigation (may still be partial)
        if (!cancelled) {
          setPoInvoicesForSummary(Array.isArray(invoices) ? invoices : []);
          setPoSummaryLoading(false);
        }
      }
    };

    loadAllForPo();
    return () => {
      cancelled = true;
    };
  }, [poNumber, invoicePayload?.po_number, invoicePayload?.poNumber, invoices]);

  // When the PO-wide invoice list loads/changes, refresh the PO Summary fields shown in the details card.
  // (Otherwise the UI can keep showing the count from the initial pending-only list.)
  useEffect(() => {
    if (!invoicePayload) {
      return;
    }
    if (poSummaryLoading) {
      // Wait for full PO invoice list, to avoid flicker in the UI.
      return;
    }
    const rawInvoice = invoicePayload._originalData || invoicePayload;
    const poSummary = buildPoInvoiceSummary({
      rawInvoice,
      poInvoicesList: poInvoicesForSummary || [],
      poDetails,
      allowRemainingFallback: false,
    });

    setInvoiceData((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        poTotalAmountFormatted: poSummary.poTotalAmountFormatted,
        alreadyClaimedAmountFormatted: poSummary.alreadyClaimedAmountFormatted,
        poInvoicesCount: poSummary.poInvoicesCount,
        reasonOfRejection: poSummary.reasonOfRejection,
      };
    });
  }, [invoicePayload, poInvoicesForSummary, poDetails, poSummaryLoading]);

  // Helper function to transform audit logs for ActivityTimeline
  const transformAuditLogsForTimeline = (logs) => {
    // Filter out SYSTEM entries - don't show them in invoice tracking
    // Filter out SYSTEM entries - don't show automated assignment / system rows in tracking
    const filteredLogs = (logs || []).filter(
      (log) => (log.performed_by_role || '').toUpperCase() !== 'SYSTEM'
    );
    return filteredLogs.map((log) => {
      // Determine the display name for the performer
      let performerName = log.performed_by_role || log.performed_by_username || 'Unknown';
      if (performerName.toUpperCase() === 'USER') {
        performerName = 'Vendor';
      }

      // Create human-readable summary from action_code and action_details
      let summary = '';
      const actionCode = log.action_code || '';
      const actionDetails = log.action_details || {};

      switch (actionCode) {
        case 'CREATED':
          summary = 'Invoice created';
          if (actionDetails.vendor) {
            summary += ` by ${actionDetails.vendor}`;
          }
          break;
        case 'ASSIGNED':
          summary = 'Invoice assigned';
          if (log.assigned_role?.name) {
            summary += ` to ${log.assigned_role.name}`;
          } else if (actionDetails.assigned_to_role_id) {
            summary += ` to role ID ${actionDetails.assigned_to_role_id}`;
          }
          break;
        case 'APPROVED':
          summary = 'Invoice approved';
          if (actionDetails.new_role_name) {
            summary += ` and forwarded to ${actionDetails.new_role_name}`;
          } else if (actionDetails.reason === 'No next role available - final approval') {
            summary += ' (final approval)';
          }
          break;
        case 'REJECTED':
          summary = 'Invoice rejected';
          break;
        case 'REVERTED':
          summary = 'Invoice reverted';
          if (actionDetails.new_role_name) {
            summary += ` to ${actionDetails.new_role_name}`;
          }
          break;
        case 'REVERTED_TO_REJECTED':
          summary = 'Invoice reverted to rejected status';
          break;
        case 'STOPPED':
          summary = 'Invoice stopped';
          break;
        case 'STARTED':
          summary = 'Invoice started';
          break;
        case 'SUBMITTED':
          summary = 'Invoice submitted';
          break;
        case 'UPDATED':
          summary = 'Invoice updated';
          break;
        default:
          summary = actionCode ? `Invoice ${actionCode.toLowerCase()}` : 'Invoice action';
      }

      if (actionDetails.comment) {
        summary += ` (Comment: ${actionDetails.comment})`;
      }

      return {
        timeStamp: log.created_at || '',
        username: performerName, // Display role instead of username
        summary: summary,
        details: actionDetails,
      };
    });
  };

  // Helper function to map invoice data
  const mapInvoiceData = (invoiceData) => {
    const formatDate = (dateString) => {
      if (!dateString) {
        return '';
      }
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
      } catch (e) {
        return dateString;
      }
    };

    const rawInvoice = invoiceData._originalData || invoiceData;
    const previousApprover =
      rawInvoice?.previous_assigned_role?.name || invoiceData.previousApprover || '';
    const currentApprover =
      rawInvoice?.assigned_role?.name ||
      invoiceData.assigned_role?.name ||
      invoiceData.assignedRole?.name ||
      invoiceData.currentApprover ||
      '';

    const poSummary = buildPoInvoiceSummary({
      rawInvoice,
      poInvoicesList: poInvoicesForSummary || [],
      poDetails,
      allowRemainingFallback: !poSummaryLoading,
    });

    const mappedInvoiceData = {
      invoiceNumber: rawInvoice.invoice_number || invoiceData.invoiceNumber || '',
      title: rawInvoice.invoice_number || invoiceData.invoiceNumber || '',
      invoiceNo: rawInvoice.invoice_number || invoiceData.invoiceNumber || '',
      category: rawInvoice.category || invoiceData.category || '',
      vendorName: rawInvoice.vendor || invoiceData.vendorName || '',
      invoiceDate: formatDate(
        rawInvoice.created_at ||
          rawInvoice.updated_at ||
          rawInvoice.invoice_date ||
          invoiceData.invoiceDate
      ),
      region: rawInvoice.region || invoiceData.region || '',
      poNumber: rawInvoice.po_number || invoiceData.poNumber || poNumber || '',
      poStatus: 'Active', // You may need to add this field to API response
      approvalStatus:
        rawInvoice.status || invoiceData.approvalStatus
          ? (rawInvoice.status || invoiceData.approvalStatus).charAt(0).toUpperCase() +
            (rawInvoice.status || invoiceData.approvalStatus).slice(1).toLowerCase()
          : '',
      status: rawInvoice.status || invoiceData.status || '',
      vendorRemarks:
        rawInvoice.vendor_remarks || rawInvoice.vendorRemarks || invoiceData.vendorRemarks || '',
      previousApprover,
      currentApprover,
      poTotalAmountFormatted: poSummary.poTotalAmountFormatted,
      alreadyClaimedAmountFormatted: poSummary.alreadyClaimedAmountFormatted,
      poInvoicesCount: poSummary.poInvoicesCount,
      reasonOfRejection: poSummary.reasonOfRejection,
    };

    const mappedBreakdownData = (rawInvoice.rings_data || []).map((ring) => ({
      name: ring.ringName || ring.sectionName || '',
      milestone: ring.paymentMilestone || ring.paymentRingMilestone || '',
      length: ring.length || '',
      unitRate: ring.unitRate || '',
      invoiceAmount: ring.amount || '',
    }));

    const mappedDocuments = [];
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';

    // Add PO file if exists
    if (rawInvoice.po_file_path) {
      const fileName = rawInvoice.po_file_path.split('/').pop() || 'PO File.pdf';
      mappedDocuments.push({
        id: 'po-file',
        name: 'PO File',
        fileName: fileName,
        filePath: rawInvoice.po_file_path,
        url: `${apiBaseUrl}/adminInvoiceManagement/invoice/document/${rawInvoice.po_file_path}`,
        type: 'application/pdf',
      });
    }

    // Add other documents
    if (rawInvoice.other_documents_paths && Array.isArray(rawInvoice.other_documents_paths)) {
      rawInvoice.other_documents_paths.forEach((docPath, index) => {
        const fileName = docPath.split('/').pop() || `Document ${index + 1}`;
        const lowerPath = docPath.toLowerCase();
        const lowerFileName = fileName.toLowerCase();

        // Determine file type based on extension
        let fileType = 'application/octet-stream';
        if (lowerPath.endsWith('.pdf') || lowerFileName.endsWith('.pdf')) {
          fileType = 'application/pdf';
        } else if (lowerPath.endsWith('.xlsx') || lowerFileName.endsWith('.xlsx')) {
          fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (lowerPath.endsWith('.xls') || lowerFileName.endsWith('.xls')) {
          fileType = 'application/vnd.ms-excel';
        } else if (lowerPath.endsWith('.csv') || lowerFileName.endsWith('.csv')) {
          fileType = 'text/csv';
        } else if (lowerPath.endsWith('.msg') || lowerFileName.endsWith('.msg')) {
          fileType = 'application/vnd.ms-outlook';
        }

        mappedDocuments.push({
          id: `other-doc-${index}`,
          name: fileName,
          fileName: fileName,
          filePath: docPath,
          // Use the authenticated endpoint for serving documents
          url: `${apiBaseUrl}/adminInvoiceManagement/invoice/document/${docPath}`,
          type: fileType,
        });
      });
    }

    return { mappedInvoiceData, mappedBreakdownData, mappedDocuments };
  };

  // Load approver roles on component mount
  useEffect(() => {
    const loadApproverRoles = async () => {
      setLoadingRoles(true);
      try {
        const roles = await getApproverRoles();
        setApproverRoles(roles);
      } catch (err) {
        console.error('Error loading approver roles:', err);
        // Don't show error to user, just log it
      } finally {
        setLoadingRoles(false);
      }
    };

    loadApproverRoles();
  }, []);

  // Filter approver roles based on invoice region and invoice_type
  useEffect(() => {
    if (!approverRoles.length || !invoiceData) {
      setFilteredApproverRoles([]);
      return;
    }

    const invoiceRegion = invoiceData.region || '';
    const normalizeInvoiceType = (val) => {
      if (!val) {
        return null;
      }
      return String(val).trim().toUpperCase().replace(/-/g, '_');
    };
    // Get invoice_type from passedInvoiceData and normalize it (handle "Non-FTTS" -> "NON_FTTS", "FTTS" -> "FTTS")
    const invoiceType = normalizeInvoiceType(passedInvoiceData?.invoice_type);

    const filtered = approverRoles.filter((role) => {
      // For RO category roles: filter by region match
      if (role.category === 'RO') {
        // Show roles that match the invoice's region, or global roles (region is null)
        if (role.region == null) {
          return true;
        } // global role - show for all regions
        return role.region === invoiceRegion;
      }

      // For PLANNING category roles: filter by invoice_type match
      if (role.category === 'PLANNING') {
        // If role has a specific invoice_type, it must match the invoice's invoice_type
        const roleInvoiceType = normalizeInvoiceType(role.invoice_type);
        if (roleInvoiceType) {
          return roleInvoiceType === invoiceType;
        }
        // If role has null invoice_type, show it only if:
        // 1. Invoice also has null invoice_type, OR
        // 2. There's no other role with the same name that matches the invoice_type
        if (!roleInvoiceType) {
          // Check if there's a role with matching invoice_type for the same role name
          const hasMatchingTypeRole = approverRoles.some(
            (r) =>
              r.category === 'PLANNING' &&
              r.name === role.name &&
              normalizeInvoiceType(r.invoice_type) === invoiceType &&
              r.id !== role.id
          );
          // Show this role if no specific match exists, or if invoice_type is also null
          return !hasMatchingTypeRole || !invoiceType;
        }
      }

      // For other categories: show all roles
      return true;
    });

    setFilteredApproverRoles(filtered);
  }, [approverRoles, invoiceData, passedInvoiceData]);

  // Load invoice data from passed state (or after missing-documents upload)
  useEffect(() => {
    const loadInvoiceData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (invoicePayload) {
          // Use passed invoice data from navigation
          const { mappedInvoiceData, mappedBreakdownData, mappedDocuments } =
            mapInvoiceData(invoicePayload);

          // Set mapped data
          setInvoiceData(mappedInvoiceData);
          setBreakdownData(mappedBreakdownData);
          setDocuments(mappedDocuments);
          setInvoicePreviewData(null); // Invoice preview can be added later if needed

          // Prefill existing reason for pending_rejection so admin can edit and reject
          const existingReason =
            invoicePayload?.reason_of_rejection ||
            invoicePayload?._originalData?.reason_of_rejection ||
            invoicePayload?.reasonOfRejection ||
            '';
          setRejectReason((existingReason || '').toString());

          // Select first document by default
          if (mappedDocuments.length > 0) {
            setSelectedDocumentIndex(0);
            setSelectedDocument(mappedDocuments[0]);
            setShowDocumentPreview(true);
          }
        } else {
          setError('Invoice data not found. Please go back and try again.');
        }
      } catch (err) {
        console.error('Error loading invoice data:', err);
        setError('Failed to load invoice data');
      } finally {
        setLoading(false);
      }
    };

    loadInvoiceData();
  }, [invoicePayload, poNumber]);

  const showAddMissingDocuments =
    invoicePayload &&
    (invoicePayload.status || invoicePayload?._originalData?.status || '')
      .toString()
      .toLowerCase()
      .trim() === 'pending_rejection';

  const handleMissingDocumentsSaved = useCallback(
    (updatedInvoiceRow) => {
      setInvoiceSource((prev) => {
        const base = prev || invoicePayload;
        if (!base) {
          return base;
        }
        return {
          ...base,
          other_documents_paths: updatedInvoiceRow?.other_documents_paths,
        };
      });
    },
    [invoicePayload]
  );

  // Fetch audit logs function - can be called on demand
  const fetchAuditLogs = useCallback(async () => {
    if (!currentInvoiceId) {
      return;
    }

    try {
      setLoadingAuditLogs(true);
      const response = await getPOInvoiceAuditLogs(currentInvoiceId);
      // Handle the API response structure: { status, message, response: [...] }
      const logs = response?.response && Array.isArray(response.response) ? response.response : [];
      setAuditLogs(logs);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  }, [currentInvoiceId]);

  // Fetch audit logs when invoice details page opens
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  // Derive approver emails from audit logs and merge into invoiceData
  useEffect(() => {
    if (!invoiceData) {
      return;
    }
    const hasPreviousApprover = Boolean(
      String(invoiceData.previousApprover || '').trim() ||
        invoicePayload?.previous_assigned_role?.id ||
        invoicePayload?.previous_assigned_to_role_id
    );
    const previousApproverEmail = hasPreviousApprover
      ? getPreviousApproverEmailFromAuditLogs(auditLogs)
      : '';
    const currentApproverEmail = getCurrentApproverEmail(
      auditLogs,
      invoiceData.currentApprover,
      invoicePayload
    );
    setInvoiceData((prev) => {
      if (!prev) {
        return prev;
      }
      if (
        prev.previousApproverEmail === previousApproverEmail &&
        prev.currentApproverEmail === currentApproverEmail
      ) {
        return prev;
      }
      return { ...prev, previousApproverEmail, currentApproverEmail };
    });
  }, [
    auditLogs,
    invoiceData?.invoiceNumber,
    invoiceData?.currentApprover,
    invoiceData?.previousApprover,
    invoicePayload,
  ]);

  const handleBack = () => {
    runWithUnsavedCommentsGuard(
      commentsSectionRef,
      () => {
        const tab = (fromTab || 'action-required').toString().trim();

        // If invoice was opened from "All Invoice" tab, go directly back to that tab
        // (avoid bouncing through the PO invoices list page).
        if (tab === 'all-invoices-full') {
          navigate(`/poWorkflowManagement?tab=${encodeURIComponent(tab)}`);
          return;
        }

        // Otherwise, go back to the PO invoices list page.
        navigate('/poWorkflowManagement/invoices', {
          state: {
            poNumber: poNumber,
            invoices: invoices,
            poDetails,
            fromTab,
          },
        });
      },
      { isNavigation: true }
    );
  };

  const handleHistory = () => {
    setHistoryModalOpen(true);
  };

  const handleAssignTo = async (role) => {
    if (!currentInvoiceId || assigningRole) {
      return;
    }
    await runWithUnsavedCommentsGuard(
      commentsSectionRef,
      async () => {
        setAssigningRole(true);
        try {
          const response = await apiClient.post(
            `/adminInvoiceManagement/invoice/by-id/${currentInvoiceId}/assign`,
            {
              assigned_to: role.id,
              role_name: role.name,
            }
          );

          const successMessage = response?.data?.message || 'Invoice assigned successfully';
          notify('success', successMessage);
          const tab = (fromTab || 'action-required').toString().trim();
          const suffix = tab && tab !== 'action-required' ? `?tab=${encodeURIComponent(tab)}` : '';
          navigate(`/poWorkflowManagement${suffix}`);
        } catch (err) {
          handleApiError(err);
        } finally {
          setAssigningRole(false);
        }
      },
      { actionLabel: 'Assign' }
    );
  };

  const handleStart = async () => {
    if (!currentInvoiceId || loadingAction) {
      return;
    }
    await runWithUnsavedCommentsGuard(
      commentsSectionRef,
      async () => {
        setLoadingAction('start');
        try {
          await startInvoice(currentInvoiceId, '');
          const tab = (fromTab || 'action-required').toString().trim();
          const suffix = tab && tab !== 'action-required' ? `?tab=${encodeURIComponent(tab)}` : '';
          navigate(`/poWorkflowManagement${suffix}`);
        } catch (err) {
          setLoadingAction(null);
        }
      },
      { actionLabel: 'Start' }
    );
  };

  const handleStop = async () => {
    if (!currentInvoiceId || loadingAction) {
      return;
    }
    await runWithUnsavedCommentsGuard(
      commentsSectionRef,
      async () => {
        setLoadingAction('stop');
        try {
          await stopInvoice(currentInvoiceId, '');
          const tab = (fromTab || 'action-required').toString().trim();
          const suffix = tab && tab !== 'action-required' ? `?tab=${encodeURIComponent(tab)}` : '';
          navigate(`/poWorkflowManagement${suffix}`);
        } catch (err) {
          setLoadingAction(null);
        }
      },
      { actionLabel: 'Stop' }
    );
  };

  const handleRejectClick = () => {
    runWithUnsavedCommentsGuard(commentsSectionRef, () => setRejectDialogOpen(true), {
      actionLabel: 'Reject',
      saveThenContinue: true,
    });
  };

  const handleRejectClose = () => {
    if (loadingAction === 'reject') {
      return;
    }
    setRejectDialogOpen(false);
  };

  const handleRejectConfirm = async () => {
    if (!currentInvoiceId || loadingAction) {
      return;
    }
    const trimmed = (rejectReason || '').trim();
    if (!trimmed) {
      notify('error', 'Reason of rejection is required');
      return;
    }
    setLoadingAction('reject');
    try {
      const resp = await rejectInvoice(currentInvoiceId, trimmed, '');
      notify('success', resp?.message || 'Invoice rejected successfully');
      const tab = (fromTab || 'action-required').toString().trim();
      const suffix = tab && tab !== 'action-required' ? `?tab=${encodeURIComponent(tab)}` : '';
      navigate(`/poWorkflowManagement${suffix}`);
    } catch (err) {
      // Error handled in function; keep dialog open for edits
      setLoadingAction(null);
    }
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
  };

  const handleDocumentClick = async (index, document) => {
    console.log('Document clicked:', index, document);

    // Revoke previous blob URL if it exists
    const previousBlobUrl = documentBlobUrl;
    if (previousBlobUrl) {
      revokeBlobUrl(previousBlobUrl);
    }

    // Clear previous state
    setDocumentBlobUrl(null);
    setLoadingDocument(true);
    setSelectedDocumentIndex(index);
    setSelectedDocument(document);
    setShowDocumentPreview(true);

    try {
      // If document has a blobUrl already, use it
      if (document.blobUrl) {
        setDocumentBlobUrl(document.blobUrl);
        setLoadingDocument(false);
        return;
      }

      // If document has a direct URL, use it
      if (document.url || document.documentUrl || document.fileUrl) {
        setLoadingDocument(false);
        return;
      }

      // Otherwise, fetch blob from backend
      if (document.id && currentInvoiceNumber) {
        const blobUrl = await getDocumentBlob(currentInvoiceNumber, document.id);
        setDocumentBlobUrl(blobUrl);
      }
    } catch (error) {
      console.error('Error loading document:', error);
      handleApiError(error);
    } finally {
      setLoadingDocument(false);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (documentBlobUrl) {
        revokeBlobUrl(documentBlobUrl);
      }
    };
  }, [documentBlobUrl]);

  if (loading) {
    return (
      <DashboardLayout padding={true}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error || !invoiceData) {
    return (
      <DashboardLayout padding={true}>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              margin: '0 auto',
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
            }}
          >
            <Alert severity="error">{error || 'Invoice not found'}</Alert>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout padding={true}>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            margin: '0 auto',
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
          }}
        >
          {/* Header */}
          <POInvoiceHeader
            invoiceNumber={invoiceData.invoiceNumber}
            onBack={handleBack}
            onHistory={handleHistory}
            onAssignTo={isViewOnlyFromAllInvoices ? undefined : handleAssignTo}
            onStart={isViewOnlyFromAllInvoices ? undefined : handleStart}
            onStop={isViewOnlyFromAllInvoices ? undefined : handleStop}
            onReject={isViewOnlyFromAllInvoices ? undefined : handleRejectClick}
            approverRoles={isViewOnlyFromAllInvoices ? [] : filteredApproverRoles}
            loadingRoles={isViewOnlyFromAllInvoices ? false : loadingRoles}
            assigningRole={isViewOnlyFromAllInvoices ? false : assigningRole}
            loadingAction={loadingAction}
            invoiceStatus={invoiceData.approvalStatus}
          />

          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <InvoiceDetailsCard invoiceData={invoiceData} />
              <BreakdownTable
                breakdownData={breakdownData}
                nameColumnLabel={
                  (
                    passedInvoiceData?.invoice_type ||
                    passedInvoiceData?.invoice_details?.invoice_type ||
                    ''
                  )
                    .toString()
                    .toUpperCase()
                    .replace(/-/g, '_') === 'NON_FTTS'
                    ? 'Section Name'
                    : 'Ring Name'
                }
              />
              <CommentsSection
                ref={commentsSectionRef}
                invoiceId={passedInvoiceData?.invoice_details?.id || passedInvoiceData?.id}
                commentByRoleId={
                  passedInvoiceData?.invoice_details?.assigned_to_role_id ||
                  passedInvoiceData?.assigned_to_role_id
                }
                commentByRoleName={passedInvoiceData?.assigned_role?.name}
                userType="admin"
              />
              <DocumentsList
                documents={documents}
                selectedIndex={selectedDocumentIndex}
                onDocumentClick={handleDocumentClick}
              />
              {showAddMissingDocuments && (
                <AddMissingDocumentsAdminSection
                  invoiceNumber={currentInvoiceNumber}
                  onSaved={handleMissingDocumentsSaved}
                  disabled={loading}
                />
              )}
            </Grid>

            {/* Right Column - Show Document Preview or Invoice Preview */}
            <Grid item xs={12} md={6}>
              {showDocumentPreview && selectedDocument ? (
                <DocumentPreview
                  key={`doc-preview-${selectedDocumentIndex}-${selectedDocument.id || selectedDocumentIndex}-${documentBlobUrl || selectedDocument.url || ''}`}
                  documentUrl={(() => {
                    // Determine if this is an Excel/CSV/MSG file
                    const isExcelOrCSVOrMsg =
                      selectedDocument.type ===
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      selectedDocument.type === 'application/vnd.ms-excel' ||
                      selectedDocument.type === 'text/csv' ||
                      selectedDocument.type === 'application/vnd.ms-outlook' ||
                      /\.(xlsx|xls|csv|msg)$/i.test(
                        selectedDocument.fileName || selectedDocument.name || ''
                      );

                    // Get the base URL
                    let baseUrl =
                      documentBlobUrl ||
                      selectedDocument.blobUrl ||
                      selectedDocument.url ||
                      selectedDocument.documentUrl ||
                      selectedDocument.fileUrl ||
                      // Only construct URL from ID if it's a real backend ID (not frontend-generated like 'other-doc-X')
                      (selectedDocument.id &&
                      !selectedDocument.id.startsWith('other-doc-') &&
                      !selectedDocument.id.startsWith('po-file')
                        ? `${process.env.REACT_APP_API_URL}/adminInvoiceManagement/invoice/${currentInvoiceNumber}/documents/${selectedDocument.id}`
                        : null);

                    // For Excel/CSV/MSG files, add ?format=pdf to convert to PDF for preview
                    if (isExcelOrCSVOrMsg && baseUrl && !baseUrl.includes('blob:')) {
                      const separator = baseUrl.includes('?') ? '&' : '?';
                      baseUrl = `${baseUrl}${separator}format=pdf`;
                    }

                    return baseUrl;
                  })()}
                  downloadUrl={(() => {
                    // For downloads, use the original file URL (without PDF conversion)
                    return (
                      documentBlobUrl ||
                      selectedDocument.blobUrl ||
                      selectedDocument.url ||
                      selectedDocument.documentUrl ||
                      selectedDocument.fileUrl ||
                      (selectedDocument.id &&
                      !selectedDocument.id.startsWith('other-doc-') &&
                      !selectedDocument.id.startsWith('po-file')
                        ? `${process.env.REACT_APP_API_URL}/adminInvoiceManagement/invoice/${currentInvoiceNumber}/documents/${selectedDocument.id}`
                        : null)
                    );
                  })()}
                  documentName={
                    selectedDocument.name ||
                    selectedDocument.fileName ||
                    selectedDocument.documentName ||
                    'Document'
                  }
                  documentType={(() => {
                    // For Excel/CSV/MSG files being previewed as PDF, return PDF type
                    const isExcelOrCSVOrMsg =
                      selectedDocument.type ===
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      selectedDocument.type === 'application/vnd.ms-excel' ||
                      selectedDocument.type === 'text/csv' ||
                      selectedDocument.type === 'application/vnd.ms-outlook' ||
                      /\.(xlsx|xls|csv|msg)$/i.test(
                        selectedDocument.fileName || selectedDocument.name || ''
                      );

                    if (isExcelOrCSVOrMsg) {
                      return 'application/pdf'; // Treat as PDF for preview
                    }
                    return selectedDocument.type || selectedDocument.mimeType || 'application/pdf';
                  })()}
                  isLoading={loadingDocument}
                />
              ) : (
                invoicePreviewData && <InvoicePreview invoicePreviewData={invoicePreviewData} />
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* History Modal */}
      <InvoiceHistoryModal
        open={historyModalOpen}
        onClose={handleCloseHistoryModal}
        invoiceId={currentInvoiceId}
        auditLogs={transformAuditLogsForTimeline(auditLogs)}
        isLoadingAuditLogs={loadingAuditLogs}
        onRefresh={fetchAuditLogs}
      />

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee' }}>Reject Invoice</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Please provide the reason of rejection. This will be emailed to the vendor.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reason of rejection"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={loadingAction === 'reject'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleRejectClose} disabled={loadingAction === 'reject'}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectConfirm}
            disabled={loadingAction === 'reject'}
          >
            {loadingAction === 'reject' ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default POInvoiceDetails;
